import { useEffect, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { BrainOrb } from './BrainOrb';
import './CommandBar.css';

export type CommandFamily = 'do' | 'go' | 'ask';

export interface Command {
  family: CommandFamily;
  label: string;
  hint?: string;
  onRun?: () => void;
}

interface CommandBarProps {
  open: boolean;
  onClose: () => void;
  defaults?: Command[];
  /** When the user types a query that doesn't match any command and
   *  presses Enter, escalate to the global Brain chat with the typed
   *  text as the seed message. AppShell wires this up. */
  onAskBrain?: (seedMessage: string) => void;
}

const familyOrder: Record<CommandFamily, number> = { do: 0, go: 1, ask: 2 };

export function CommandBar({ open, onClose, defaults = [], onAskBrain }: CommandBarProps) {
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  if (!open) return null;

  const trimmed = query.trim();
  const filtered = (defaults
    .filter((c) => c.label.toLowerCase().includes(trimmed.toLowerCase())))
    .sort((a, b) => familyOrder[a.family] - familyOrder[b.family]);

  // Surface "Ask Brain about: <query>" at the top of the list when the
  // query is non-empty AND we have an escalation target. Lets the coach
  // type a free-form question and Enter to drop into the chat surface.
  const askBrainItem: Command | null = trimmed && onAskBrain
    ? {
        family: 'ask',
        label: `Ask Brain: "${trimmed}"`,
        hint: 'open chat',
        onRun: () => onAskBrain(trimmed),
      }
    : null;
  const items = askBrainItem ? [askBrainItem, ...filtered] : filtered;

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'Enter' && items[0]) {
      items[0].onRun?.();
      onClose();
    }
  };

  return (
    <div className="command-bar__scrim" onMouseDown={onClose}>
      <div
        className="command-bar"
        role="dialog"
        aria-label="Brain Bar"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="command-bar__head">
          <BrainOrb size="sm" ariaLabel="Brain Bar" />
          <input
            autoFocus
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="DO · GO · ASK"
            className="command-bar__input"
          />
        </div>
        <ul className="command-bar__list">
          {items.map((c, i) => (
            <li key={`${c.family}-${c.label}-${i}`}>
              <button
                type="button"
                className="command-bar__item"
                onClick={() => {
                  c.onRun?.();
                  onClose();
                }}
              >
                <span className="command-bar__family" data-family={c.family}>
                  {c.family.toUpperCase()}
                </span>
                <span className="command-bar__label">{c.label}</span>
                {c.hint && <span className="command-bar__hint">{c.hint}</span>}
              </button>
            </li>
          ))}
          {items.length === 0 && (
            <li className="command-bar__empty">no matches</li>
          )}
        </ul>
      </div>
    </div>
  );
}

export function useBrainBar(initial = false) {
  const [open, setOpen] = useState(initial);
  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => {
      const isCmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (isCmdK) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
  return { open, setOpen };
}
